import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "lib/utils";
import * as React from "react";

const buttonVariants = cva("btn", {
  variants: {
    variant: {
      default: "btn-primary !text-[#ffffff]",
      secondary: "btn-outline btn-primary hover:!text-[#ffffff]",
      destructive: "btn-error !text-[#ffffff]",
      ghost: "btn-ghost",
      outline: "btn-outline",
      link: "btn-link",
      warning: "btn-warning !text-[#ffffff]",
      success: "btn-success !text-[#ffffff]",
      info: "btn-info !text-[#ffffff]",
      accent: "btn-accent !text-[#ffffff]",
      /** DaisyUI 素の `btn` (color 未指定、neutral 配色)。pagination 等で利用 */
      neutral: "",
    },
    size: {
      default: "",
      xs: "btn-xs",
      sm: "btn-sm",
      md: "",
      lg: "btn-lg",
      icon: "btn-square btn-sm",
    },
    isOutline: {
      true: "btn-outline",
    },
    width: {
      narrow: "w-40",
      full: "w-full",
    },
    isJoinItem: {
      true: "join-item",
    },
    isActive: {
      true: "btn-active",
    },
    shape: {
      circle: "btn-circle",
      square: "btn-square",
    },
  },
  compoundVariants: [
    // isOutline 時は solid 用に強制していた !white を剥がし、variant の色を文字色にする
    {
      variant: "default",
      isOutline: true,
      className: "!text-primary hover:!text-primary-content",
    },
    {
      variant: "destructive",
      isOutline: true,
      className: "!text-error hover:!text-error-content",
    },
    {
      variant: "warning",
      isOutline: true,
      className: "!text-warning hover:!text-warning-content",
    },
    {
      variant: "success",
      isOutline: true,
      className: "!text-success hover:!text-success-content",
    },
    {
      variant: "info",
      isOutline: true,
      className: "!text-info hover:!text-info-content",
    },
    {
      variant: "accent",
      isOutline: true,
      className: "!text-accent hover:!text-accent-content",
    },
  ],
  defaultVariants: { variant: "default", size: "default" },
});

export type ButtonProps = {
  asChild?: boolean;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "className"> &
  VariantProps<typeof buttonVariants>;

/**
 * 共通 Button コンポーネント。CVA で variant / size / fullWidth / isJoinItem を制御する。
 * className prop は意図的に受け取らない。レイアウトは親側で、装飾は variant props で表現する。
 * @param {ButtonProps} props Button の props
 * @param {React.Ref<HTMLButtonElement>} ref 転送される ref
 * @return {JSX.Element} Button 要素
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant,
      size,
      isOutline,
      width,
      isJoinItem,
      isActive,
      shape,
      asChild = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            isOutline,
            width,
            isJoinItem,
            isActive,
            shape,
          }),
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
